import { BASE_API_URL_V2 } from "../constants";
import fpFetch from "./fpFetch";
import { errorFromResponse } from "./apiError";
import { setProductTimeout } from "@/utils/productTimers";

/** Auth-aware fetch of a file-serving API path. Returns the bytes plus the
 *  filename the server named in `Content-Disposition`.
 *
 *  Browsers don't carry the `Authorization: Bearer …` header through a plain
 *  `<a href download>`, so any authenticated download has to pull the bytes
 *  here (fpFetch reads the stytch session cookie and sets the header) and
 *  hand the browser a blob URL instead. */
export async function fetchApiFile(
  path: string
): Promise<{ blob: Blob; filename: string }> {
  const response = await fpFetch(`${BASE_API_URL_V2}${path}`, {
    "x-finpilot-auth": true,
  });
  if (!response.ok) {
    // Shared so a network-level failure reads as a sentence rather than
    // `undefined: undefined` (#11634). The error carries the status, so callers
    // can branch on it without parsing the text.
    throw await errorFromResponse(response);
  }
  return {
    blob: await response.blob(),
    filename:
      filenameFromDisposition(
        response.headers.get("content-disposition") || ""
      ) ?? "download",
  };
}

/** The filename a `Content-Disposition` names, or null when it names none.
 *
 * `filename*` wins when present, per RFC 6266 and because it is the only form
 * that survives a quote or a non-ASCII character: the quoted form has no way to
 * carry them that every reader agrees on, so a name like `odd"quote.md` used to
 * come back truncated at the quote. Exported for its own test — the parse is
 * what decides the name the browser saves. */
export function filenameFromDisposition(disposition: string): string | null {
  const extended = disposition.match(
    /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/
  );
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed percent-escape is not a reason to fail the download; fall
      // through to the plain form.
    }
  }
  const quoted = disposition.match(/filename\s*=\s*"((?:[^"\\]|\\.)*)"/);
  if (quoted) return quoted[1].replace(/\\(.)/g, "$1");
  const bare = disposition.match(/filename\s*=\s*([^;]+)/);
  return bare ? bare[1].trim() : null;
}

/** Fetch an authenticated file and save it, via a blob URL behind a hidden
 *  anchor. The revoke is deferred so the browser finishes writing the file. */
export async function downloadApiFile(
  path: string,
  fallbackName: string
): Promise<void> {
  const { blob, filename } = await fetchApiFile(path);
  saveBlob(blob, filename || fallbackName);
}

/** Save bytes already in hand as `filename`, via a blob URL behind a hidden
 *  anchor. The revoke is deferred so the browser finishes writing the file. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setProductTimeout(() => URL.revokeObjectURL(url), 60_000);
}
